import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Building2, Mail, Lock, FileText, ArrowLeft, CheckCircle2, Loader2, Info } from 'lucide-react';
import GuardedDialog from './GuardedDialog';
import PasswordInput from './PasswordInput';
import useFormDirtyState from '../hooks/useFormDirtyState';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SignupModal — Popup d'inscription en 2 étapes
 * 
 * Étape 1: Formulaire (SIRET + Email + MDP + CGU)
 * Étape 2: Vérification OTP (code 6 chiffres)
 * 
 * Utilise GuardedDialog pour protection contre fermeture accidentelle.
 */
const SignupModal = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { loginDirect } = useAuth();
  const { hasUnsavedChanges, setDirty, resetDirty } = useFormDirtyState();

  // Step management: 'form' | 'verify'
  const [step, setStep] = useState('form');

  // ── Step 1: Signup form state ──
  const [formData, setFormData] = useState({
    siret: '',
    email: '',
    password: '',
    confirmPassword: '',
    cgu_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ── SIRET lookup state ──
  const [companyData, setCompanyData] = useState(null); // Données récupérées
  const [siretLookupLoading, setSiretLookupLoading] = useState(false);
  const [siretLookupError, setSiretLookupError] = useState('');
  const siretLookupRef = useRef(null); // Debounce timer

  // ── Step 2: OTP verification state ──
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Reset complet à CHAQUE ouverture — garantit un formulaire vierge
  useEffect(() => {
    if (open) {
      setStep('form');
      setFormData({ siret: '', email: '', password: '', confirmPassword: '', cgu_accepted: false });
      setErrors({});
      setCompanyData(null);
      setSiretLookupLoading(false);
      setSiretLookupError('');
      setRegisteredEmail('');
      setCode(['', '', '', '', '', '']);
      setVerifyError('');
      setVerifyLoading(false);
      setCooldown(0);
      setLoading(false);
      resetDirty();
      if (siretLookupRef.current) clearTimeout(siretLookupRef.current);
    }
  }, [open, resetDirty]);

  // Reset everything when modal closes
  const handleClose = useCallback((val) => {
    onOpenChange(val);
  }, [onOpenChange]);

  // ── Step 1: Form handlers ──
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirty();
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Trigger SIRET lookup when 14 digits reached
    if (field === 'siret') {
      const cleaned = value.replace(/[\s-]/g, '');
      if (siretLookupRef.current) clearTimeout(siretLookupRef.current);
      if (cleaned.length === 14 && /^\d{14}$/.test(cleaned)) {
        siretLookupRef.current = setTimeout(() => lookupSiret(cleaned), 400);
      } else {
        setCompanyData(null);
        setSiretLookupError('');
        setSiretLookupLoading(false);
      }
    }
  };

  const lookupSiret = async (siret) => {
    setSiretLookupLoading(true);
    setSiretLookupError('');
    setCompanyData(null);
    try {
      const res = await fetch(`${API_URL}/api/company/lookup-siret?siret=${siret}`);
      const data = await res.json();
      if (!res.ok) {
        setSiretLookupError(data.detail || 'Erreur de recherche');
        return;
      }
      if (data.found) {
        setCompanyData(data);
      } else {
        setSiretLookupError(data.message || "Établissement introuvable");
      }
    } catch {
      setSiretLookupError("Les informations n'ont pas pu être récupérées. Vous pourrez les renseigner manuellement.");
    } finally {
      setSiretLookupLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    const siretClean = formData.siret.replace(/[\s-]/g, '');
    if (!siretClean) {
      errs.siret = 'Le SIRET est obligatoire';
    } else if (!/^\d{14}$/.test(siretClean)) {
      errs.siret = 'Le SIRET doit contenir exactement 14 chiffres';
    }
    if (!formData.email.trim()) {
      errs.email = "L'email professionnel est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = "Format d'email invalide";
    }
    if (!formData.password) {
      errs.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 6) {
      errs.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    if (formData.password !== formData.confirmPassword) {
      errs.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    if (!formData.cgu_accepted) {
      errs.cgu_accepted = "Vous devez accepter les Conditions Générales d'Utilisation";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siret: formData.siret.replace(/[\s-]/g, ''),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          cgu_accepted: true,
          // Données SIRET pré-remplies (si récupérées)
          ...(companyData?.found ? {
            raison_sociale: companyData.raison_sociale,
            adresse_magasin: companyData.adresse,
            code_postal: companyData.code_postal,
            ville: companyData.ville,
            date_creation_magasin: companyData.date_creation,
          } : {})
        })
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.detail) {
          toast.error(data.detail);
          if (data.detail.includes('email') || data.detail.includes('Email')) {
            setErrors({ email: data.detail });
          } else if (data.detail.includes('SIRET') || data.detail.includes('siret')) {
            setErrors({ siret: data.detail });
          }
        }
        return;
      }

      toast.success('Compte créé ! Vérifiez votre email.');
      setRegisteredEmail(formData.email.trim().toLowerCase());
      setStep('verify');
      resetDirty();
    } catch {
      toast.error('Erreur serveur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: OTP handlers ──
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setVerifyError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setVerifyError('Veuillez saisir les 6 chiffres du code');
      return;
    }

    setVerifyLoading(true);
    setVerifyError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail, code: fullCode })
      });
      const data = await res.json();

      if (!res.ok) {
        setVerifyError(data.detail || 'Code incorrect');
        return;
      }

      if (data.token && data.user) {
        loginDirect(data.token, data.user);
        toast.success('Email vérifié avec succès !');
        handleClose(false);
        // La redirection est gérée par LandingPage useEffect (selon account_status)
      }
    } catch {
      setVerifyError('Erreur serveur. Veuillez réessayer.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail })
      });
      if (res.ok) {
        toast.success('Un nouveau code a été envoyé');
        setCooldown(60);
        setCode(['', '', '', '', '', '']);
        setVerifyError('');
        inputRefs.current[0]?.focus();
      }
    } catch {
      toast.error('Erreur lors du renvoi');
    } finally {
      setResending(false);
    }
  };

  // Determine isDirty: form step with changes, or verify step (don't lose OTP progress)
  const isDirty = step === 'form' ? hasUnsavedChanges : step === 'verify';

  return (
    <GuardedDialog
      open={open}
      onOpenChange={handleClose}
      isDirty={isDirty}
      className="max-w-md max-h-[90vh] overflow-y-auto"
    >
      {step === 'form' ? (
        <>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--ludo-text)' }} data-testid="signup-modal-title">
              Créer un compte
            </DialogTitle>
            <p className="text-sm mt-1" style={{ color: 'var(--ludo-text-secondary)' }}>
              Inscription rapide pour les professionnels du jeu de société
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4 mt-2" data-testid="signup-modal-form">
            {/* SIRET */}
            <div className="space-y-1.5">
              <Label htmlFor="modal-siret" className="flex items-center gap-1 text-sm">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                SIRET <span className="text-red-500">*</span>
              </Label>
              <Input
                id="modal-siret"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Ex: 12345678900012"
                value={formData.siret}
                onChange={e => handleChange('siret', e.target.value)}
                maxLength={14}
                className={errors.siret ? 'border-red-500' : ''}
                data-testid="signup-modal-siret"
              />
              {errors.siret && <p className="text-xs text-red-500">{errors.siret}</p>}
              {!errors.siret && !siretLookupLoading && !companyData && !siretLookupError && (
                <p className="text-xs text-muted-foreground">
                  Saisissez les 14 chiffres de votre SIRET pour récupérer automatiquement les informations de votre établissement.
                </p>
              )}

              {/* SIRET Lookup: Loading */}
              {siretLookupLoading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="siret-lookup-loading">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Recherche des informations de votre établissement…
                </div>
              )}

              {/* SIRET Lookup: Error */}
              {siretLookupError && (
                <p className="text-xs text-amber-600" data-testid="siret-lookup-error">{siretLookupError}</p>
              )}

              {/* SIRET Lookup: Result */}
              {companyData?.found && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-1" data-testid="siret-lookup-result">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs font-medium text-green-700">Établissement identifié</span>
                  </div>
                  <p className="text-sm font-medium text-green-900" data-testid="siret-lookup-raison-sociale">{companyData.raison_sociale}</p>
                  {companyData.adresse && (
                    <p className="text-xs text-green-700">{companyData.adresse}</p>
                  )}
                  <p className="text-xs text-green-700">
                    {companyData.code_postal} {companyData.ville}
                  </p>
                  {companyData.date_creation && (
                    <p className="text-xs text-green-600">
                      Date de création {companyData.date_creation_type === 'entreprise' ? "de l'entreprise" : "de l'établissement"} : {companyData.date_creation}
                    </p>
                  )}
                  <div className="flex items-start gap-1 mt-1 pt-1 border-t border-green-200">
                    <Info className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-green-600 italic">
                      Prérempli automatiquement à partir du SIRET. Ces informations seront enregistrées dans votre profil et resteront modifiables.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="modal-email" className="flex items-center gap-1 text-sm">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                Email professionnel <span className="text-red-500">*</span>
              </Label>
              <Input
                id="modal-email"
                type="email"
                autoComplete="off"
                placeholder="votremagasin@exemple.fr"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
                data-testid="signup-modal-email"
              />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>

            {/* Mot de passe */}
            <div className="space-y-1.5">
              <Label htmlFor="modal-password" className="flex items-center gap-1 text-sm">
                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                Mot de passe <span className="text-red-500">*</span>
              </Label>
              <PasswordInput
                id="modal-password"
                autoComplete="new-password"
                placeholder="Minimum 6 caractères"
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                className={errors.password ? 'border-red-500' : ''}
                data-testid="signup-modal-password"
              />
              {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
            </div>

            {/* Confirmation */}
            <div className="space-y-1.5">
              <Label htmlFor="modal-confirm" className="flex items-center gap-1 text-sm">
                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                Confirmer le mot de passe <span className="text-red-500">*</span>
              </Label>
              <PasswordInput
                id="modal-confirm"
                autoComplete="new-password"
                placeholder="Retapez votre mot de passe"
                value={formData.confirmPassword}
                onChange={e => handleChange('confirmPassword', e.target.value)}
                className={errors.confirmPassword ? 'border-red-500' : ''}
                data-testid="signup-modal-confirm-password"
              />
              {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
            </div>

            {/* CGU */}
            <div className="space-y-1.5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="modal-cgu"
                  checked={formData.cgu_accepted}
                  onCheckedChange={v => handleChange('cgu_accepted', v)}
                  data-testid="signup-modal-cgu-checkbox"
                />
                <Label htmlFor="modal-cgu" className="text-sm leading-relaxed cursor-pointer">
                  J'accepte les{' '}
                  <a href="/page/cgu" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                    Conditions Générales d'Utilisation
                  </a>
                  {' '}<span className="text-red-500">*</span>
                </Label>
              </div>
              {errors.cgu_accepted && <p className="text-xs text-red-500">{errors.cgu_accepted}</p>}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="signup-modal-submit"
            >
              {loading ? 'Création en cours...' : 'Créer un compte'}
            </Button>
          </form>

          {/* Login link */}
          <p className="text-center text-sm mt-3" style={{ color: 'var(--ludo-text-secondary)' }}>
            Déjà un compte ?{' '}
            <button
              type="button"
              onClick={() => {
                handleClose(false);
                navigate('/login');
              }}
              className="font-semibold hover:underline text-primary"
              data-testid="signup-modal-login-link"
            >
              Se connecter
            </button>
          </p>
        </>
      ) : (
        /* ── Step 2: OTP Verification ── */
        <>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setStep('form');
                  setCode(['', '', '', '', '', '']);
                  setVerifyError('');
                }}
                className="p-1 rounded-md hover:bg-muted transition-colors"
                data-testid="signup-modal-back-button"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <DialogTitle className="text-xl font-bold" style={{ color: 'var(--ludo-text)' }} data-testid="verify-modal-title">
                Vérification email
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Confirmation badge */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-green-800">Compte créé avec succès</p>
                <p className="text-green-600 mt-0.5">
                  Un code à 6 chiffres a été envoyé à <span className="font-medium">{registeredEmail}</span>
                </p>
              </div>
            </div>

            <form onSubmit={handleVerify} className="space-y-5" data-testid="verify-modal-form">
              <div className="space-y-2">
                <Label className="text-center block text-sm text-muted-foreground">
                  Saisissez le code reçu par email
                </Label>

                {/* 6-digit input */}
                <div className="flex justify-center gap-2" onPaste={handlePaste}>
                  {code.map((digit, i) => (
                    <Input
                      key={i}
                      ref={el => inputRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      className="w-11 h-12 text-center text-xl font-bold"
                      data-testid={`verify-modal-code-input-${i}`}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
              </div>

              {/* Error */}
              {verifyError && (
                <div className="text-sm text-red-600 text-center bg-red-50 rounded-lg p-3" data-testid="verify-modal-error">
                  {verifyError}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                disabled={verifyLoading || code.join('').length !== 6}
                data-testid="verify-modal-submit"
              >
                {verifyLoading ? 'Vérification...' : 'Valider'}
              </Button>
            </form>

            {/* Resend */}
            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                className="text-sm text-primary hover:underline disabled:text-gray-400 disabled:no-underline"
                data-testid="verify-modal-resend"
              >
                {cooldown > 0
                  ? `Renvoyer un code (${cooldown}s)`
                  : resending
                    ? 'Envoi en cours...'
                    : 'Renvoyer un code'}
              </button>
            </div>
          </div>
        </>
      )}
    </GuardedDialog>
  );
};

export default SignupModal;
